import { CountryCode } from '@dayetopia/types';
import { graphql, useStaticQuery } from 'gatsby';
import { ImageField, TextField } from '@components/shared/vms/CommonTypes';
import { ProductCode, ProductCodeKey } from '@contracts';
import { filterProducts } from '@utils/product';
import useLanguageData from '@utils/useLanguageData';
import { ProductData } from './storefront';

export interface SimpleProductData {
  simple_product_code: ProductCode;
  simple_product_title: TextField;
  simple_product_description: TextField;
  simple_product_image: ImageField;
  simple_link: TextField;
  simple_background: TextField;
  show_in_countries: TextField;
}

export const query = graphql`
  query getShopSimpleProducts {
    allPrismicEcommerceShopProducts {
      nodes {
        lang
        data {
          simple_products {
            simple_product_code
            simple_product_title {
              text
            }
            simple_product_description {
              text
            }
            simple_product_image {
              alt
              gatsbyImageData
              url
            }
            simple_link {
              text
            }
            simple_background {
              text
            }
            show_in_countries {
              text
            }
          }
        }
      }
    }
  }
`;

function convertSimpleProducts(simpleProducts: SimpleProductData[]): Partial<ProductData>[] {
  return simpleProducts.map((simpleProduct) => ({
    product_code: simpleProduct.simple_product_code,
    product_title: simpleProduct.simple_product_title,
    product_description: simpleProduct.simple_product_description,
    product_image: simpleProduct.simple_product_image,
    link: simpleProduct.simple_link,
    background: simpleProduct.simple_background,
    show_in_countries: simpleProduct.show_in_countries
  }));
}

export const getSimpleShopProducts = (
  country?: CountryCode,
  ignoreByCode?: ProductCodeKey,
  customProducts?: { product_id: ProductCodeKey }[]
): Partial<ProductData>[] => {
  const { allPrismicEcommerceShopProducts } = useStaticQuery(query);
  const { nodes } = allPrismicEcommerceShopProducts;
  const { pageData: shopProductsData } = useLanguageData(nodes);
  let finalSimpleShopProducts = shopProductsData?.data?.simple_products;

  if (!finalSimpleShopProducts) {
    return [];
  }

  finalSimpleShopProducts = convertSimpleProducts(finalSimpleShopProducts);

  if (country) {
    finalSimpleShopProducts = filterProducts(country, finalSimpleShopProducts);
  }

  if (ignoreByCode) {
    finalSimpleShopProducts = finalSimpleShopProducts.filter((product: ProductData) => (product.product_code ?? '') !== ignoreByCode);
  }

  if (customProducts && customProducts?.length && customProducts[0].product_id) {
    const finalCustomProducts: ProductData[] = [];

    customProducts.forEach((customProduct) => {
      const foundCustomProduct = finalSimpleShopProducts.find((product: ProductData) => product.product_code === customProduct.product_id);

      if (foundCustomProduct) {
        finalCustomProducts.push(foundCustomProduct);
      }
    });

    finalSimpleShopProducts = finalCustomProducts;
  }

  return finalSimpleShopProducts;
};
